import * as yup from 'yup';

import { propertyContractBaseSchema } from '@/features/Contract/Property/contractBaseSchema';
import {
  hasAtLeastTwoRentals,
  hasRentalCoverage,
  hasRentCoverage,
  hasThreeRentals,
} from '@/features/Sparten/res/formConfigs/sharedRenderConditions';
import {
  TYPE_OF_BUILDING,
  TYPE_OF_BUILDING_USAGE,
} from '@/features/Sparten/res/formConfigs/types';
import { locales } from '@/features/validation/locales';
import { genericStoreFactory } from '@/utils/genericStoreFactory/genericStoreFactory';
import { testSpecialChars } from '@/utils/strings/strings';
import { hasPreviousDamage } from '@/features/Contract/Property/utils';
import { useInputStore } from '@/features/Sparten';
import { getSimpleChoiceMeta } from '@/utils/getSimpleChoiceMeta/getSimpleChoiceMeta';
import {
  SIMPLE_CHOICE,
  SIMPLE_CHOICE_META,
} from '@/features/ChoicesLists/Simple/types';

yup.setLocale(locales);

const vorversicherungVnPartner = yup.object({
  enabled: yup.boolean().default(false),
  versicherungsnummer: yup.string().when('enabled', {
    is: (val: boolean) => val,
    then: yup.string().required(),
  }),
  gesellschaft: yup
    .string()
    .default(undefined)
    .when('enabled', {
      is: (val: boolean) => val,
      then: yup.string().required(),
    }),
  kuendigender: yup
    .string()
    .default(undefined)
    .when('enabled', {
      is: (val: boolean) => val,
      then: yup.string().required(),
    }),
});

const additionalInformationSchema = yup.object({
  allgemein: yup.object({
    vorversicherungVersicherungsnehmer: vorversicherungVnPartner,
    vorversicherungPartner: vorversicherungVnPartner,
  }),
  mieten: yup.object({
    gebaeudeart: yup
      .string()
      .default(TYPE_OF_BUILDING.APARTMENT)
      .when({
        is: () => hasRentCoverage.value,
        then: yup
          .string()
          .required()
          .oneOf([TYPE_OF_BUILDING.APARTMENT, TYPE_OF_BUILDING.HOUSE]),
      }),
    nutzungsart: yup
      .string()
      .default(TYPE_OF_BUILDING_USAGE.OWNER)
      .when({
        is: () => hasRentCoverage.value,
        then: yup
          .string()
          .required()
          .oneOf([TYPE_OF_BUILDING_USAGE.OWNER, TYPE_OF_BUILDING_USAGE.TENANT]),
      }),
  }),
  vermietung: yup.object({
    vermietungObjektAnschrift0: yup.string().when({
      is: () => hasRentalCoverage.value,
      then: yup.string().required(),
    }),
    vermietungObjektAnschrift1: yup.string().when({
      is: () => hasAtLeastTwoRentals.value,
      then: yup.string().required(),
    }),
    vermietungObjektAnschrift2: yup.string().when({
      is: () => hasThreeRentals.value,
      then: yup.string().required(),
    }),
  }),
  verkehr: yup.object({
    verkehrFahrzeugKennzeichen0: yup.string().default(''),
    verkehrFahrzeugKennzeichen1: yup.string().default(''),
    verkehrFahrzeugKennzeichen2: yup.string().default(''),
    verkehrFahrzeugKennzeichen3: yup.string().default(''),
    verkehrFahrzeugKennzeichen4: yup.string().default(''),
    verkehrFahrzeugKennzeichen5: yup.string().default(''),
  }),
});

export const previousDamageSchema = yup.object({
  bereich: yup
    .string()
    .default('')
    .when({
      is: () => hasPreviousDamage.value,
      then: (schema) =>
        // required() fängt den unberührten Leerwert, der test() die bewusst
        // nicht getroffene Auswahl (AsyncSelect setzt dann den emptyDefault).
        schema
          .required()
          .test(
            'isValidChoice',
            locales.mixed?.required as string,
            (val) =>
              !getSimpleChoiceMeta(
                SIMPLE_CHOICE.RES_VORSCHADENBEREICH,
                SIMPLE_CHOICE_META.INVALID,
              ).includes(val ?? ''),
          ),
    }),
  entschaedigungszahlung: yup
    .number()
    .default(null)
    .nullable()
    .when({
      is: () => hasPreviousDamage.value,
      then: (schema) => schema.required().integer('Bitte runden Sie den Wert.'),
    }),
  schadensmeldung: yup
    .date()
    .nullable()
    .default(null)
    .when({
      is: () => hasPreviousDamage.value,
      then: (schema) => schema.required(),
    }),
  memofeldAnsprueche: yup
    .string()
    .default('')
    .when({
      is: () => hasPreviousDamage.value,
      // Geht 1:1 an den Anbieter (SMIT). Zeichen ausserhalb von Latin-1 (ausser €) wie der
      // typografische Gedankenstrich lassen den Antrag dort scheitern (VGL-7272).
      // Nur bei sichtbarem Feld pruefen, sonst blockiert ein unsichtbarer Fehler den Antrag.
      then: (schema) => schema.required().test(testSpecialChars),
    }),
});

const resPreviousDamageSchema = yup.object({
  bestehenAnsprueche: yup.bool().default(false),
  anzahl: yup
    .number()
    .nullable()
    .default(null)
    .when({
      is: () => hasPreviousDamage.value,
      then: (schema) =>
        schema.when(() => {
          const { vorschaden } = useInputStore();
          const hasMoreThanTwoDamages = getSimpleChoiceMeta(
            SIMPLE_CHOICE.VORSCHADEN,
            SIMPLE_CHOICE_META.VORSCHADEN_MORE_THAN_TWO_DAMAGES,
          ).includes(vorschaden);

          if (hasMoreThanTwoDamages) {
            return schema.required().min(3);
          }
          return schema.required().min(1).default(1);
        }),
    }),
  list: yup.array().default([]).of(previousDamageSchema).required(),
});

export type ResPreviousDamage = yup.Asserts<typeof resPreviousDamageSchema>;

export const contractSchema = propertyContractBaseSchema
  .omit(['vorschaden']) // make sure we completely replace (not merge) the vorschaden schema here
  .concat(
    yup.object({
      vorschaden: resPreviousDamageSchema,
      zusaetzlicheAngaben: additionalInformationSchema,
    }),
  );

export type ContractStore = yup.Asserts<typeof contractSchema>;

export const useResContractStore = genericStoreFactory(
  () => contractSchema.getDefault() as ContractStore,
  'ResContractStore',
);
